/**
 * intakeLinkStore — seam for the M5-S2 intake-link generation + smart-channel
 * dispatch (AD-020/AD-022). Lazy-imports the Firebase SDK so the pure channel
 * logic below stays SDK-free and testable.
 *
 * Server contract (firebase-functions/functions/avbp_intake_links.js):
 *   generateIntakeLink     { cid, pid, service, propertyId? } → { linkId, url }
 *   sendIntakeLinkViaWA    { linkId } → { ok, channel:'wa' }
 *   sendIntakeLinkViaEmail { linkId } → { ok, channel:'email', messageId }
 *
 * Smart channel (AD-020): whatsapp present → WA primary; email-only → email;
 * both → both; neither → error (no auto-send; staff must add a channel).
 */

export const INTAKE_SERVICES = ['L1A', 'L1B', 'L2B', 'L3A', 'L3B'] as const;
export type IntakeService = (typeof INTAKE_SERVICES)[number];

export interface ClientChannels {
  name: string;
  whatsapp: string | null;
  email: string | null;
}

export interface ChannelDetection {
  hasWa: boolean;
  hasEmail: boolean;
  channels: ('wa' | 'email')[];
  primary: 'wa' | 'email' | null;
  none: boolean;
}

/**
 * PURE — mirrors the server-side detectChannels (AD-020). WA-first.
 */
export function detectChannels(c: {
  whatsapp?: string | null;
  email?: string | null;
}): ChannelDetection {
  const hasWa = typeof c.whatsapp === 'string' && c.whatsapp.trim().length > 0;
  const hasEmail = typeof c.email === 'string' && c.email.trim().length > 0;
  const channels: ('wa' | 'email')[] = [];
  if (hasWa) channels.push('wa');
  if (hasEmail) channels.push('email');
  const primary = hasWa ? 'wa' : hasEmail ? 'email' : null;
  return { hasWa, hasEmail, channels, primary, none: channels.length === 0 };
}

/** Lazy-import a callable (same idiom as features/teaminbox/lib/emailSend). */
async function callable<TReq, TRes>(name: string, data: TReq): Promise<TRes> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<TReq, TRes>(functions, name);
  const res = await fn(data);
  return res.data;
}

/** Read clients/{cid} for name + whatsapp + email (R3 field names). */
export async function loadClientChannels(cid: string): Promise<ClientChannels> {
  const [{ db }, { doc, getDoc }] = await Promise.all([
    import('./firebase'),
    import('firebase/firestore'),
  ]);
  const snap = await getDoc(doc(db, 'clients', cid));
  const d = (snap.exists() ? snap.data() : {}) as Record<string, unknown>;
  const whatsapp =
    (typeof d.whatsapp === 'string' && d.whatsapp) ||
    (typeof d.phone === 'string' && d.phone) ||
    null;
  return {
    name: (typeof d.name === 'string' && d.name) || cid,
    whatsapp: whatsapp ? String(whatsapp).trim() || null : null,
    email:
      typeof d.email === 'string' && d.email.trim()
        ? d.email.trim()
        : null,
  };
}

export interface GenerateResult {
  linkId: string;
  url: string;
}

export function generateIntakeLink(args: {
  cid: string;
  /** Required for report tiers (project-scoped). OPTIONAL for `birth_only`
   *  (Flag-#2, AD-008/AD-019: persons are client-scoped, not project-scoped, so
   *  a birth_only link needs only cid + personId; pid is optional context). */
  pid?: string;
  /** A report tier OR 'birth_only' (M5-S5, a targeted single-person birth link). */
  service: IntakeService | 'birth_only';
  propertyId?: string;
  /** Required when service === 'birth_only': the target person record. */
  personId?: string;
}): Promise<GenerateResult> {
  return callable<typeof args, GenerateResult>('generateIntakeLink', args);
}

export function sendIntakeLinkViaWA(
  linkId: string,
): Promise<{ ok: boolean; channel: 'wa' }> {
  return callable<{ linkId: string }, { ok: boolean; channel: 'wa' }>(
    'sendIntakeLinkViaWA',
    { linkId },
  );
}

export function sendIntakeLinkViaEmail(
  linkId: string,
): Promise<{ ok: boolean; channel: 'email'; messageId?: string | null }> {
  return callable<
    { linkId: string },
    { ok: boolean; channel: 'email'; messageId?: string | null }
  >('sendIntakeLinkViaEmail', { linkId });
}

/**
 * STEP 3 (M5 Flag B fold-back) — write ONE internal project-thread note when a
 * link is successfully sent. PORTAL-side via the canonical `postInternalNote`
 * (NOT a CF — avoids a competing functions writer on the unified-thread path
 * during the parallel M5 sessions). The note is `clientVisible:false`,
 * `authorRole:'staff'` (the team author; AuthorRole has no 'team' literal),
 * `channel:'portal'`, `direction:'out'` — exactly the shape postMessage writes,
 * which the deployed `projects/{pid}/messages` create=isTeam() rule allows.
 * Best-effort: a failed note must never block the (already-successful) send.
 */
export async function logIntakeLinkSent(args: {
  pid: string;
  cid: string;
  clientName: string;
  channels: string[]; // the channels ACTUALLY sent, e.g. ['WhatsApp','email']
  actor: { uid: string | null; name: string };
}): Promise<void> {
  const { postInternalNote } = await import('./conversation');
  const chans = args.channels.length ? args.channels.join(' + ') : 'portal';
  await postInternalNote(args.pid, {
    authorName: args.actor.name,
    authorUid: args.actor.uid ?? 'system',
    authorRole: 'staff',
    body: `Intake link sent to ${args.clientName} via ${chans}`,
    contactId: args.cid,
  });
}
